import { select } from '@angular-redux/store';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, Output, QueryList, Renderer2, ViewChildren } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ItemData } from 'sharedFeatures/form';
import { infoPopUpComponent } from 'sharedFeatures/sharedGui/components/infoPopUp';
import { DialogCreator } from 'sharedFeatures/sharedGui/components/window';
import { CommonApp } from 'sharedFeatures/sharedModels';
import { AccountsModel } from "sharedFeatures/sharedModels/xero/accountsModel";
import { LinkHeadingFiltersListModel } from "sharedFeatures/sharedModels/xero/linkHeadingFiltersListModel";
import { XeroLinkDescriptor } from "sharedFeatures/sharedModels/xero/xeroLinkDescriptor";
import { XeroMatchedAccount } from "sharedFeatures/sharedModels/xero/xeroMatchedAccount";
import { SharedGuiSmallModule } from '../../../../sharedGuiSmall/sharedGuiSmall.module';

const CUSTOM_CHECK_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => XeroLinksComponent),
    multi: true
};

@Component({
    selector: 'xeroLinks',
    templateUrl: 'xeroLinks.component.html',
    styleUrls: ['xeroLinks.component.less'],
    providers: [CUSTOM_CHECK_CONTROL_VALUE_ACCESSOR],
    standalone: true,
    imports: [SharedGuiSmallModule, infoPopUpComponent]
})

export class XeroLinksComponent implements ControlValueAccessor {

    public _xeroLinks: Array<XeroLinkDescriptor> | undefined;
    public _xeroLinkData: Array<Array<ItemData>> = new Array<Array<ItemData>>();

    public isLoading: boolean = false;

    @Input()
    public xeroAccounts: AccountsModel = {};

    @Input()
    public xeroFilters: LinkHeadingFiltersListModel = new LinkHeadingFiltersListModel();

    @Input()
    private valueChanges: { changed: boolean } = { changed: false };

    @Input()
    public showInfoPopup: boolean = true;

    @Input()
    public showLabel: boolean = true;

    @Output()
    private change = new EventEmitter<any>();

    @Input()
    public linkSizes: Array<number> | undefined;

    @Input()
    linkHeadingCls: string = '';

    @Input()
    parentName: string = '';

    @Input()
    formRowCls: string = '';

    // Unsubscribe event
    private storeUnsubscribe = new Subject<void>();

    @select(['appData', 'appComponent'])
    readonly appComponent$!: Observable<any>;
    appComponent: any = null;

    @select(['accountAppData', 'accountAppComponent'])
    readonly accountAppComponent$!: Observable<any>;
    accountAppComponent: any = null;

    @ViewChildren(infoPopUpComponent)
    infoPopupItems!: QueryList<infoPopUpComponent>;

    private linksSeparator = ` `;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private chDet: ChangeDetectorRef,
    ) {

    }


    ngOnInit(): void {
        this.subscribeReduxStore();
    }

    ngOnChanges() {
    }

    ngAfterViewInit() {
    }

    ngOnDestroy() {
        // Unsubscribe from the redux store
        this.storeUnsubscribe.next();
        this.storeUnsubscribe.complete();
    }

    private subscribeReduxStore(): void {
        this.appComponent$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => this.appComponent = x);

        this.accountAppComponent$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => this.accountAppComponent = x);


    }

    closeOtherInfoPanels(id: string) {
        this.infoPopupItems.forEach(x => {
            if (x.pFieldID !== id)
                x.close();
        });
    }

    private buildXeroLinksSource(): Array<Array<ItemData>> {

        let result = Array<Array<ItemData>>();
        let allXeros = this.xeroAccounts;
        let allFilters = this.xeroFilters;
        let xeroLinks = this._xeroLinks;

        if (xeroLinks)
            xeroLinks.forEach((link) => {

                let arr = Array<ItemData>();
                let curFilter = link.LinkHeading;

                arr.push({
                    id: null,
                    value: "Unmatched",
                    disabled: false
                });

                if (allXeros && xeroLinks) {

                    let cnt = 0;
                    xeroLinks.forEach((link: XeroLinkDescriptor) => {

                        let accs = null;

                        const xeroAccHeading = link.XeroAccountHeading;
                        if (xeroAccHeading)
                            accs = allXeros[xeroAccHeading];

                        link.width = this.linkSizes?this.linkSizes[cnt]:undefined;

                        if (accs) {
                            accs.forEach((acc) => {

                                if (!arr.find(x => x.id === acc.AccountCode)) {

                                    if (this.xeroFilters && link.isUseFilter) {
                                        let accFilters = null;

                                        if (xeroAccHeading)
                                            accFilters = allFilters[xeroAccHeading];

                                        if (accFilters) {
                                            let accFilter = accFilters[acc.AccountSubHeading];

                                            if (accFilter) {
                                                var linkFilters = accFilter.split(';');

                                                let filterMatch = false;
                                                linkFilters.forEach((filter) => {
                                                    if (filter == curFilter) { filterMatch = true; return; }
                                                });

                                                if (!filterMatch)
                                                    return;
                                            }
                                        }
                                    }

                                    arr.push({
                                        id: acc.AccountCode,
                                        value: "{0}{1}{2}".format(
                                            acc.DisplayCode ? acc.DisplayCode : acc.AccountCode,
                                            this.linksSeparator,
                                            acc.DisplayName ? acc.DisplayName : acc.AccountName),
                                        disabled: false
                                    });
                                }
                            });
                        }

                        cnt++;
                    });
                }

                result.push(arr);

            });
        return result;
    }

    private getAvailableAccounts(accHeading: string): Array<XeroMatchedAccount> {
        let result = this.xeroAccounts && this.xeroAccounts[accHeading] ? this.xeroAccounts[accHeading].slice() : new Array<XeroMatchedAccount>();

        if (result.length > 0) {
            // process special cases and other account list exceptions rules
            if (accHeading == "Cost of Sales") {
                // we must include Staff costs/Salaries accounts
                let additionalAccs = this.xeroAccounts["Staff costs"];
                if (additionalAccs) {
                    additionalAccs.forEach((acc) => {
                        if (acc.AccountSubHeading == "Salaries")
                            result.push(acc);
                    });
                }
            }
            else if (accHeading == "Staff costs") {
                // we must include Cost of sales/Staff
                let additionalAccs = this.xeroAccounts["Cost of Sales"];
                if (additionalAccs) {
                    additionalAccs.forEach((acc) => {
                        if (acc.AccountSubHeading == "Staff")
                            result.push(acc);
                    });
                }
            }
        }

        return result;
    }

    getApp(): CommonApp {
        if (this.appComponent)
            return this.appComponent;
        else if (this.accountAppComponent)
            return this.accountAppComponent;

        throw new Error('Architecture error.');
    }

    getDialogCreator(): DialogCreator {
        return this.getApp();
    }

    public onXeroLinkChange({ id, value }: ItemData, linkField: string, accHeading: string): void {

        let accs = this.getAvailableAccounts(accHeading).filter(x => x.AccountCode == id);
        //let accs = this.xeroAccounts[accHeading];

        if (!accs)
            return;
       
        const acc = id ? accs.find(x => x.AccountCode == id) : accs[0];

        let link: XeroLinkDescriptor | undefined;

        if (this._xeroLinks)
            link = this._xeroLinks.find(x => x.LinkField == linkField);

        if (link) {
            if (acc && value)
            {
                if (link.XeroAccountCode == acc.AccountCode)
                    return;

                link.XeroAccountId = acc.AccountID;
                link.XeroAccountCode = acc.AccountCode;
                link.XeroAccountName = acc.AccountName;
            }
            else
            {
                if (link.XeroAccountCode == null)
                    return;

                link.XeroAccountId = '';
                link.XeroAccountCode = '';
                link.XeroAccountName = '';
            }


            if (!this.isLoading) {
                this.valueChanges.changed = true;
                this.change.emit(this._xeroLinks);
            }

        }
    }

    public revertChanges(): void {
        this.valueChanges.changed = false;
    }

    public saveChanges(): void {
        this.valueChanges.changed = false;
    }

    get value(): Array<XeroLinkDescriptor> | undefined { return this._xeroLinks; };

    set value(v: Array<XeroLinkDescriptor> | undefined) {

        if (v !== this._xeroLinks) {
            this._xeroLinks = v;
            //this.onChange(v);
        }

    }

    writeValue(value: Array<XeroLinkDescriptor>) {
        this._xeroLinks = value;

        if (value) {
            this._xeroLinkData = this.buildXeroLinksSource();
            //this.onChange(value);
        }
    }

    onChange = (_:Function) => { };
    onTouched = () => { };
    registerOnChange(fn: (_: any) => void): void { this.onChange = fn; }
    registerOnTouched(fn: () => void): void { this.onTouched = fn; }

}
